import React from "react";

const ProjectInikaEULA = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 text-gray-200">
      <h1 className="text-3xl font-bold mb-4 text-white">
        END USER LICENSE AGREEMENT
      </h1>
      <h2 className="text-2xl font-semibold mb-6 text-gray-300">
        Project Inika QuickBooks Implementation
      </h2>

      <p className="mb-4 text-gray-400">
        <strong>Last Updated: February 20, 2025</strong>
      </p>

      <p className="mb-4">
        This End User License Agreement ("Agreement" or "EULA") is a binding
        legal agreement between you (either an individual or a single entity)
        ("Licensee" or "You") and Project Inika ("Licensor"), regarding your use
        of QuickBooks software and related services (collectively, the
        "Software") provided by Project Inika.
      </p>

      <p className="mb-8 p-4 bg-gray-800 rounded-lg text-gray-300">
        PLEASE READ THIS EULA CAREFULLY BEFORE USING THE SOFTWARE. BY
        INSTALLING, ACCESSING, OR USING THE SOFTWARE, YOU AGREE TO BE BOUND BY
        THE TERMS OF THIS AGREEMENT. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT
        INSTALL, ACCESS, OR USE THE SOFTWARE.
      </p>

      <div className="space-y-8">
        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            1. LICENSE GRANT
          </h3>
          <p className="mb-4">
            Subject to the terms and conditions of this Agreement, Licensor
            grants you a limited, non-exclusive, non-transferable,
            non-sublicensable license to use the Software solely for your
            internal business purposes. This license is specific to you and
            cannot be shared with others.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            2. RESTRICTIONS
          </h3>
          <p className="mb-4">You shall not:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>
              Copy, modify, adapt, translate, or create derivative works of the
              Software
            </li>
            <li>
              Reverse engineer, decompile, disassemble, or attempt to derive the
              source code of the Software
            </li>
            <li>
              Rent, lease, loan, sell, sublicense, distribute, transmit, or
              otherwise transfer the Software
            </li>
            <li>
              Remove or alter any proprietary notices, labels, or marks from the
              Software
            </li>
            <li>
              Use the Software in any manner that violates applicable laws or
              regulations
            </li>
            <li>
              Use the Software to infringe upon the rights of any third party
            </li>
          </ul>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            3. OWNERSHIP
          </h3>
          <p className="mb-4">
            The Software is licensed, not sold. Licensor retains all right,
            title, and interest in and to the Software, including all
            intellectual property rights. Nothing in this Agreement constitutes
            a transfer or conveyance of any right, title, or interest in the
            Software, except for the limited license granted herein.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            4. SUBSCRIPTION AND PAYMENT
          </h3>
          <div className="subsection">
            <p className="mb-4">
              <strong>4.1 Subscription:</strong> Your use of the Software is
              subject to a subscription. The subscription period, fees, and
              payment terms are specified at the time of purchase.
            </p>
            <p className="mb-4">
              <strong>4.2 Payment:</strong> All fees are due in advance and are
              non-refundable, except as expressly provided in this Agreement.
            </p>
            <p className="mb-4">
              <strong>4.3 Taxes:</strong> You are responsible for all taxes
              associated with your use of the Software, excluding taxes based on
              Licensor's net income.
            </p>
          </div>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            5. UPDATES AND MAINTENANCE
          </h3>
          <p className="mb-4">
            Licensor may, from time to time, provide updates, upgrades, bug
            fixes, or maintenance releases for the Software. These updates may
            be automatic or may require manual installation. Your continued use
            of the Software following any update constitutes your acceptance of
            the updated Software.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            6. TERM AND TERMINATION
          </h3>
          <div className="subsection">
            <p className="mb-4">
              <strong>6.1 Term:</strong> This Agreement commences on the date
              you first use the Software and continues until terminated as
              provided herein.
            </p>
            <p className="mb-4">
              <strong>6.2 Termination by You:</strong> You may terminate this
              Agreement at any time by ceasing all use of the Software and
              removing it from your systems.
            </p>
            <p className="mb-4">
              <strong>6.3 Termination by Licensor:</strong> Licensor may
              terminate this Agreement immediately if you breach any provision
              of this Agreement.
            </p>
            <p className="mb-4">
              <strong>6.4 Effect of Termination:</strong> Upon termination, you
              must cease all use of the Software and destroy all copies of the
              Software in your possession.
            </p>
          </div>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            7. CONFIDENTIALITY
          </h3>
          <p className="mb-4">
            You acknowledge that the Software contains valuable trade secrets
            and confidential information of Licensor. You agree to maintain the
            confidentiality of the Software and not disclose any information
            about the Software to any third party.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            8. DISCLAIMERS
          </h3>
          <p className="p-4 bg-gray-800 rounded-lg text-gray-300">
            THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND,
            EXPRESS OR IMPLIED. LICENSOR DISCLAIMS ALL WARRANTIES, INCLUDING BUT
            NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
            FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. LICENSOR DOES NOT
            WARRANT THAT THE SOFTWARE WILL MEET YOUR REQUIREMENTS OR THAT THE
            OPERATION OF THE SOFTWARE WILL BE UNINTERRUPTED OR ERROR-FREE.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            9. LIMITATION OF LIABILITY
          </h3>
          <p className="p-4 bg-gray-800 rounded-lg text-gray-300">
            IN NO EVENT SHALL LICENSOR BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
            LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF
            USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
            ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
            OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT
            OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF
            SUCH DAMAGE.
          </p>
          <p className="mt-4 p-4 bg-gray-800 rounded-lg text-gray-300">
            LICENSOR'S TOTAL LIABILITY FOR ANY DIRECT DAMAGES SHALL NOT EXCEED
            THE AMOUNT PAID BY YOU FOR THE SOFTWARE DURING THE TWELVE (12)
            MONTHS PRECEDING THE EVENT GIVING RISE TO THE LIABILITY.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            10. INDEMNIFICATION
          </h3>
          <p className="mb-4">
            You agree to indemnify, defend, and hold harmless Licensor and its
            officers, directors, employees, agents, and affiliates from and
            against any claims, liabilities, damages, losses, costs, expenses,
            or fees (including reasonable attorneys' fees) arising from or
            relating to your use of the Software or your violation of this
            Agreement.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            11. COMPLIANCE WITH LAWS
          </h3>
          <p className="mb-4">
            You agree to comply with all applicable laws, regulations, and
            ordinances relating to your use of the Software, including but not
            limited to export control laws.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            12. GOVERNING LAW AND DISPUTE RESOLUTION
          </h3>
          <p className="mb-4">
            This Agreement shall be governed by and construed in accordance with
            the laws of [YOUR JURISDICTION], without giving effect to any
            principles of conflicts of law. Any dispute arising out of or
            relating to this Agreement shall be resolved through binding
            arbitration in accordance with the rules of the American Arbitration
            Association. The arbitration shall take place in [YOUR CITY, STATE],
            and the language of the arbitration shall be English.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            13. GENERAL PROVISIONS
          </h3>
          <div className="subsection">
            <p className="mb-4">
              <strong>13.1 Entire Agreement:</strong> This Agreement constitutes
              the entire agreement between you and Licensor regarding the
              subject matter hereof and supersedes all prior or contemporaneous
              agreements, understandings, and communications, whether written or
              oral.
            </p>
            <p className="mb-4">
              <strong>13.2 Amendment:</strong> Licensor reserves the right to
              amend this Agreement at any time by posting the amended terms on
              its website or by providing notice to you. Your continued use of
              the Software after such amendment constitutes your acceptance of
              the amended terms.
            </p>
            <p className="mb-4">
              <strong>13.3 Severability:</strong> If any provision of this
              Agreement is held to be invalid or unenforceable, such provision
              shall be struck and the remaining provisions shall be enforced.
            </p>
            <p className="mb-4">
              <strong>13.4 Waiver:</strong> The failure of Licensor to enforce
              any right or provision of this Agreement shall not constitute a
              waiver of such right or provision.
            </p>
            <p className="mb-4">
              <strong>13.5 Assignment:</strong> You may not assign or transfer
              this Agreement or any rights hereunder without the prior written
              consent of Licensor. Licensor may freely assign this Agreement.
            </p>
            <p className="mb-4">
              <strong>13.6 Force Majeure:</strong> Licensor shall not be liable
              for any delay or failure to perform resulting from causes outside
              its reasonable control.
            </p>
            <p className="mb-4">
              <strong>13.7 Survival:</strong> Sections 3, 7, 8, 9, 10, 12, and
              13 shall survive termination of this Agreement.
            </p>
          </div>
        </div>

        <div className="mt-8 border-t border-gray-700 pt-8">
          <h3 className="text-xl font-semibold mb-4 text-white">
            14. CONTACT INFORMATION
          </h3>
          <p className="mb-4">
            If you have any questions about this Agreement, please contact us
            at:
          </p>
          <address className="text-gray-300 not-italic">
            Project Inika
            <br />
            823 Congress Ave, Austin, TX 78701, Suite for Industrious c/o
            Project Inika
            <br />
            ykothari@inika.app
          </address>
        </div>
      </div>

      <p className="mt-8 text-center text-gray-400 p-4 bg-gray-800 rounded-lg">
        By installing, accessing, or using the Software, you acknowledge that
        you have read this Agreement, understand it, and agree to be bound by
        its terms and conditions.
      </p>
    </div>
  );
};

export default ProjectInikaEULA;
